import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { categoryLabel } from "@/lib/categories";
import { mediaUrl, validatePromo } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { promoErrorText } from "@/lib/promoText";
import { PromoBox } from "@/components/PromoBox";
import { FreeDeliveryProgress } from "@/components/CartExtras";
import { DI, DProductCard, PATH, WI, dsvg } from "@/components/desktop/desktopUi";
import type { PromoPreview } from "@/types";

const TRASH = dsvg('<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"/>', 20, 1.4);

/** Checkout steps shown in the right panel (cart = 1, delivery = 2, payment = 3). */
export function CheckoutSteps({ step }: { step: 1 | 2 | 3 }) {
  const { t } = useI18n();
  const labels = [t("desktop.stepCart"), t("desktop.stepDelivery"), t("desktop.stepPayment")];
  return (
    <div className="dco-steps">
      {labels.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <hr />}
          <span data-state={i + 1 < step ? "done" : i + 1 === step ? "now" : "todo"}><i>{i + 1 < step ? "✓" : i + 1}</i>{l}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** "Мы рядом на каждом этапе" block (reference). */
export function NearBlock() {
  const { t } = useI18n();
  return (
    <div className="dco-near">
      <h3 className="dk-serif">{t("desktop.nearTitle")}</h3>
      <div>
        <span>{WI(PATH.truck, 36)}<b>{t("desktop.svcDelivery")}</b>{t("desktop.svcDeliveryS")}</span>
        <span>{WI('<path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z"/><path d="m9 12 2 2 4-4"/>', 36)}<b>{t("desktop.nearPay")}</b>Payme, Click, Uzum</span>
        <span>{WI('<path d="M5 15v-3a7 7 0 0 1 14 0v3M5 15a2 2 0 0 0 2 2h1v-5H7a2 2 0 0 0-2 2zM19 15a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2z"/>', 36)}<b>{t("desktop.nearSupport")}</b>{t("desktop.nearSupportS")}</span>
        <span>{WI('<path d="M7 12l3 3 7-7M3 12l4 4M12 3l3 3"/>', 36)}<b>{t("profile.b2b")}</b>{t("desktop.nearB2b")}</span>
      </div>
    </div>
  );
}

/** Desktop cart — reference layout; same cart state, promo preview and checkout flow as the Mini App. */
export function DesktopCart() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const { delivery } = useConfig();
  const { lines, linesWithProducts, subtotalUzs, totalItems, updateQuantity, removeItem, promoCode, bundleLines, bundleLinesWithData, updateBundleQuantity } = useCart();
  const [preview, setPreview] = useState<PromoPreview | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const isEmpty = linesWithProducts.length === 0 && bundleLinesWithData.length === 0;

  // Same live promo preview as the Mini App cart (final check happens at checkout).
  useEffect(() => {
    if (!promoCode || (lines.length === 0 && bundleLines.length === 0)) {
      setPreview(null);
      setPromoError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      validatePromo(promoCode, lines, "yandex", bundleLines)
        .then((p) => { if (!cancelled) { setPreview(p); setPromoError(null); } })
        .catch((err) => { if (!cancelled) { setPreview(null); setPromoError(promoErrorText(t, locale, err)); } });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [promoCode, lines, bundleLines, t, locale]);

  const discount = preview && preview.type !== "free_delivery" ? preview.discountUzs : 0;
  const freeDelivery = (delivery.freeThresholdUzs > 0 && subtotalUzs - discount >= delivery.freeThresholdUzs) || preview?.type === "free_delivery";
  const total = Math.max(0, subtotalUzs - discount);
  const inCart = new Set(linesWithProducts.map((l) => l.product.id));
  const addons = products.filter((p) => p.orderMode === "buy_now" && !inCart.has(p.id) && (p.variants[0]?.stock ?? 0) > 0).slice(0, 4);

  if (isEmpty) {
    return (
      <div className="dk-wrap dco-empty">
        <h1 className="dk-serif">{t("cart.title")}</h1>
        <p>{t("cart.emptyHint")}</p>
        <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("cart.goToCatalog")} {DI.arrow}</button>
      </div>
    );
  }

  return (
    <div className="dco">
      <div className="dco-layout">
        <div>
          <div className="dc-crumbs"><button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <span>{t("cart.title")}</span></div>
          <div className="dco-head">
            <div>
              <h1 className="dk-serif">{t("desktop.yourCart")}</h1>
              <div className="dco-sub">{t("desktop.cartSummary", { count: totalItems })} <b>{formatUzs(subtotalUzs, locale)}</b></div>
            </div>
            <button className="dk-more" onClick={() => navigate({ name: "catalog" })}>{t("desktop.continueShopping")} {DI.arrow}</button>
          </div>

          <div className="dco-box dco-lines">
            {linesWithProducts.map((l) => (
              <div key={`${l.productId}-${l.variantId}`} className="dco-line">
                <button className="dco-line__img" onClick={() => navigate({ name: "product", slug: l.product.slug })}>
                  {l.product.images[0] && <img src={mediaUrl(l.product.images[0], 200)} alt="" />}
                </button>
                <div className="dco-line__info">
                  <small>{categoryLabel(l.product, t)}</small>
                  <button className="dk-serif" onClick={() => navigate({ name: "product", slug: l.product.slug })}>{l.product.name}</button>
                  <span>{l.variant.label}</span>
                </div>
                <div className="dco-line__price">{formatUzs(l.variant.priceUzs, locale)}</div>
                <span className="dco-qty">
                  <button onClick={() => updateQuantity(l.productId, l.variantId, Math.max(1, l.quantity - 1))} aria-label="−">−</button>
                  <b>{l.quantity}</b>
                  <button onClick={() => updateQuantity(l.productId, l.variantId, Math.min(l.variant.stock || 99, l.quantity + 1))} disabled={l.quantity >= (l.variant.stock || 99)} aria-label="+">+</button>
                </span>
                <div className="dco-line__total">{formatUzs(l.lineTotalUzs, locale)}</div>
                <button className="dco-line__del" onClick={() => removeItem(l.productId, l.variantId)} aria-label={t("desktop.remove")}>{TRASH}</button>
              </div>
            ))}
            {bundleLinesWithData.map((b) => (
              <div key={b.bundleId} className="dco-line">
                <span className="dco-line__img">{(b.bundle.image || b.bundle.items[0]?.image) && <img src={mediaUrl(b.bundle.image || b.bundle.items[0]?.image || "", 200)} alt="" />}</span>
                <div className="dco-line__info">
                  <small>{t("desktop.bundle")}</small>
                  <span className="dk-serif dco-line__name">{b.bundle.title}</span>
                  <span>{t("bundles.cartMeta", { count: b.bundle.items.length })}</span>
                </div>
                <div className="dco-line__price" />
                <span className="dco-qty">
                  <button onClick={() => updateBundleQuantity(b.bundleId, Math.max(1, b.quantity - 1))} aria-label="−">−</button>
                  <b>{b.quantity}</b>
                  <button onClick={() => updateBundleQuantity(b.bundleId, Math.min(Math.max(b.quantity, b.bundle.available), b.quantity + 1))} aria-label="+">+</button>
                </span>
                <div className="dco-line__total">{formatUzs(b.lineTotalUzs, locale)}</div>
                <button className="dco-line__del" onClick={() => updateBundleQuantity(b.bundleId, 0)} aria-label={t("desktop.remove")}>{TRASH}</button>
              </div>
            ))}
          </div>

          <div className="dco-box dco-promo">
            {WI('<path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9z"/><circle cx="8" cy="8" r="1.5"/>', 28)}
            <PromoBox error={promoError} />
          </div>
        </div>

        <aside className="dco-box dco-summary">
          <h2 className="dk-serif">{t("desktop.checkoutTitle")}</h2>
          <CheckoutSteps step={1} />
          <div className="dco-totals">
            <div><span>{t("desktop.goods", { count: totalItems })}</span><b>{formatUzs(subtotalUzs, locale)}</b></div>
            <div><span>{t("desktop.promoDiscount")}</span><b>{discount > 0 ? `− ${formatUzs(discount, locale)}` : formatUzs(0, locale)}</b></div>
            <div><span>{t("desktop.deliveryRow")}</span><b>{freeDelivery ? t("desktop.free") : t("desktop.atCheckout")}</b></div>
            <FreeDeliveryProgress subtotal={subtotalUzs - discount} threshold={delivery.freeThresholdUzs} />
            <div className="dco-total"><span>{t("cart.total")}</span><b className="dk-serif">{formatUzs(total, locale)}</b></div>
            <button className="dk-btn dco-go" onClick={() => navigate({ name: "checkout" })}>{t("desktop.toCheckout")} {DI.arrow}</button>
            <p className="dco-legal">
              {t("desktop.legalPrefix")} <button onClick={() => navigate({ name: "post", slug: "offer" })}>{t("profile.offer")}</button> {t("desktop.and")} <button onClick={() => navigate({ name: "post", slug: "privacy" })}>{t("profile.privacy")}</button>.
            </p>
          </div>
          <NearBlock />
        </aside>
      </div>

      {addons.length > 0 && (
        <section className="dco-addons">
          <div className="dco-addons__text">
            <h2 className="dk-serif">{t("desktop.addMore")}</h2>
            <p>{t("desktop.addMoreText")}</p>
            <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCoffee")} {DI.arrow}</button>
          </div>
          <div className="dco-addons__grid">{addons.map((p) => <DProductCard key={p.id} product={p} imgHeight={120} />)}</div>
        </section>
      )}
    </div>
  );
}

/**
 * Desktop checkout frame: the Mini App's own checkout form (delivery, address, slots,
 * gift, certificate, payment — unchanged logic) on the left, steps and the order on the right.
 */
export function DesktopCheckoutFrame({ children }: { children: React.ReactNode }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { linesWithProducts, bundleLinesWithData, subtotalUzs, totalItems } = useCart();
  return (
    <div className="dco">
      <div className="dco-layout">
        <div>
          <div className="dc-crumbs"><button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <button onClick={() => navigate({ name: "cart" })}>{t("cart.title")}</button> › <span>{t("desktop.checkoutTitle")}</span></div>
          <h1 className="dk-serif dco-h1">{t("desktop.checkoutTitle")}</h1>
          <div className="dco-box dco-form">{children}</div>
        </div>
        <aside className="dco-box dco-summary">
          <h2 className="dk-serif">{t("desktop.yourOrder")}</h2>
          <CheckoutSteps step={2} />
          <div className="dco-mini">
            {linesWithProducts.map((l) => (
              <div key={`${l.productId}-${l.variantId}`}>
                {l.product.images[0] && <img src={mediaUrl(l.product.images[0], 200)} alt="" />}
                <span><b>{l.product.name}</b><small>{l.variant.label} × {l.quantity}</small></span>
                <em>{formatUzs(l.lineTotalUzs, locale)}</em>
              </div>
            ))}
            {bundleLinesWithData.map((b) => (
              <div key={b.bundleId}><span><b>{b.bundle.title}</b><small>× {b.quantity}</small></span><em>{formatUzs(b.lineTotalUzs, locale)}</em></div>
            ))}
          </div>
          <div className="dco-totals"><div><span>{t("desktop.goods", { count: totalItems })}</span><b>{formatUzs(subtotalUzs, locale)}</b></div></div>
          <p className="dco-legal">{t("desktop.checkoutHint")}</p>
          <NearBlock />
        </aside>
      </div>
    </div>
  );
}
